<!-- Keeping — where the money and the family access live. Free until three
     recipes are kept, then one payment of $49, for life, no subscription.
     Reached only from the settings control on the shelf. -->
<script setup>
import { ref, computed, onMounted } from 'vue';
import {
  IonPage, IonHeader, IonToolbar, IonButtons, IonBackButton, IonContent,
} from '@ionic/vue';
import { recipes, recipesReady } from '../store.js';
import { toast } from '../core/util.js';
import { state } from '../core/state.js';
import { paywallAvailable } from '../../addons/paywall/prices.js';
import { startCheckout } from '../../addons/paywall/index.js';
// signInAvailable lives in firebase-config.js, not index.js. index.js reads
// it internally to gate its own boot() and settingsRow(), but never
// re-exports it — importing it from index.js instead would silently resolve
// to undefined and this screen would think sign-in was never available.
import { signInAvailable } from '../../addons/auth/firebase-config.js';
import { signIn, signOut } from '../../addons/auth/index.js';
import { handwriting, setHandwriting } from '../lib/hand.js';
import { skin, setSkin, SKINS } from '../lib/skin.js';
import HdIcon from '../components/HdIcon.vue';

const FREE_LIMIT = 3;

/* Off until a real one-time price exists. See the note in the template. */
const SHOW_PRICING = false;

const loaded = ref(false);
onMounted(async () => {
  await recipesReady;
  loaded.value = true;
});

const keptCount = computed(() => recipes.value.filter((r) => r.status === 'kept').length);

// The Stripe price ids in addons/paywall/prices.js are both empty strings in
// this build, so paywallAvailable() is false and nothing here can actually
// take a card. Worth naming out loud rather than hiding: even filled in,
// that addon checks out a Stripe *subscription* (monthly/annual) through the
// Firebase extension — there is no one-time "lifetime" price shape wired up
// anywhere in this codebase, which is what "$49 for life" actually needs.
// The owner's call is to ship trial-only for now, so this button stays
// reachable and honest about that rather than disabled into silence or faked
// into working.
const paymentsReady = paywallAvailable();

function unlock() {
  if (!paymentsReady) {
    toast("Payments aren't connected in this build — nothing was charged.");
    return;
  }
  // Best-effort only, and only reachable if the addon is ever configured: it
  // still has no lifetime price key, just monthly/annual.
  startCheckout('monthly');
}

const signInReady = signInAvailable();
const user = computed(() => state.user);
function doSignIn() { signIn(); }
function doSignOut() { signOut(); }
</script>

<template>
  <IonPage>
    <IonHeader class="hd-bar">
      <IonToolbar>
        <IonButtons slot="start">
          <IonBackButton default-href="/" text="Shelf" />
        </IonButtons>
      </IonToolbar>
    </IonHeader>

    <IonContent :fullscreen="true" class="hd-content">
      <div v-if="!loaded" class="hd-page waiting" aria-hidden="true"></div>

      <div v-else class="hd-page keeping">
        <!-- CALLED SETTINGS, because that is what it is right now.
             
             The plan names this screen `keeping`, and the word was earned: it
             was where you paid once to KEEP the collection for life, and
             choosing who could open it was the smaller half. With pricing off,
             the commercial half is gone and what is left is two settings — so
             a page titled "Keeping" would be a name explaining a feature the
             reader cannot see.
             
             The route id stays `keeping` so the plan and the code still agree;
             only the visible title follows the content. When SHOW_PRICING goes
             back on, the name goes with it. -->
        <h1 class="title">{{ SHOW_PRICING ? 'Keeping' : 'Settings' }}</h1>

        <!-- THE COMMERCIAL BLOCK IS OFF.
             
             It said "One payment of $49, for life" above a button that could
             not charge anyone, and a paragraph explaining that it could not.
             Three sentences to tell someone about a price that does not exist
             yet — on the screen whose name is the promise.
             
             Nothing is hidden that a person could act on: the paywall addon
             has no lifetime price wired up at all, so there was never a
             purchase behind it. When there is one, flip SHOW_PRICING and the
             block returns with the real numbers, and the trial count with it.
             Until then the screen is about what it can actually do. -->
        <section v-if="SHOW_PRICING" class="block">
          <p v-if="keptCount < FREE_LIMIT" class="status">
            <span class="hd-figure count">{{ keptCount }}</span> of your three free recipes kept.
          </p>
          <p v-else class="status">
            All three free recipes are kept. Unlocking keeps every recipe after this one too.
          </p>

          <p class="price">One payment of <span class="hd-figure">$49</span>, for life. No subscription.</p>

          <button
            type="button"
            class="unlock"
            :aria-disabled="paymentsReady ? 'false' : 'true'"
            @click="unlock"
          >
            Unlock for life
          </button>

          <p v-if="!paymentsReady" class="honest">
            Payments aren't connected in this build — there is no lifetime price configured yet, so this
            button can't actually charge anyone. Nothing will be charged if you tap it.
          </p>
        </section>

        <!-- THE PAPER IS SHOWN, NOT DESCRIBED.
             
             "Exercise book / Recipe tin" as two words in a list asks somebody
             to imagine the difference and then commit to it; a paragraph
             explaining the difference is the thing this app was pulled up for.
             Two small drawings of the actual paper answer it in the time it
             takes to look, and cost one line of type between them. -->
        <h2 class="hd-label">How a recipe looks</h2>
        <div class="skins">
          <button
            v-for="s in SKINS"
            :key="s.id"
            type="button"
            class="skin"
            :class="{ 'is-on': skin === s.id }"
            :aria-pressed="skin === s.id ? 'true' : 'false'"
            :aria-label="s.label"
            @click="setSkin(s.id)"
          >
            <!-- Decoration: the label below already names the choice, and a
                 screen reader has nothing to gain from the ruling. -->
            <span class="preview" :class="`is-${s.id}`" aria-hidden="true">
              <span v-if="s.id === 'tin'" class="filed-card"></span>
            </span>
            <span class="skin-label">
              <HdIcon v-if="skin === s.id" name="check" :size="14" />
              {{ s.label }}
            </span>
          </button>
        </div>

        <!-- SETTINGS ARE ROWS, NOT ESSAYS.
             
             This screen used to explain itself in paragraphs: four lines on why
             the printed face is easier to read in a kitchen, four more on what
             device-level access means. All of it true, none of it what someone
             opening Settings is here for — they came to flip something and
             leave. A setting that needs a paragraph is usually a setting whose
             label is wrong.
             
             One row per thing: what it is, its state, and at most one short
             line where the state alone would mislead. -->
        <ul class="rows">
          <li class="row">
            <button
              type="button"
              class="row-btn"
              role="switch"
              :aria-checked="handwriting ? 'true' : 'false'"
              @click="setHandwriting(!handwriting)"
            >
              <span class="row-label">Handwriting</span>
              <span class="row-state hd-figure">{{ handwriting ? 'On' : 'Off' }}</span>
            </button>
          </li>

          <li v-if="signInReady" class="row">
            <button type="button" class="row-btn" @click="user ? doSignOut() : doSignIn()">
              <span class="row-label">Family access</span>
              <span class="row-state hd-figure">{{ user ? 'Sign out' : 'Sign in' }}</span>
            </button>
          </li>
          <li v-else class="row">
            <div class="row-btn is-static">
              <span class="row-label">Family access</span>
              <span class="row-state hd-figure">This device only</span>
            </div>
          </li>
        </ul>
      </div>
    </IonContent>
  </IonPage>
</template>

<style scoped>
.waiting { min-height: 100%; }

.keeping { padding: 24px 20px 64px calc(var(--hd-margin-w) + 20px); min-height: 100%; position: relative; }
.keeping::before {
  content: '';
  position: absolute;
  top: 0; bottom: 0; left: var(--hd-margin-w);
  width: 1px;
  background: var(--hd-margin);
}

.title { margin: 0 0 20px; font-size: var(--hd-t-title); font-weight: 500; line-height: 1.1; letter-spacing: -0.01em; }

.rows { margin: 0; padding: 0; list-style: none; max-width: 30em; }
.rows .row + .row { border-top: 1px solid var(--hd-feint); }

.row-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  min-height: 56px;
  padding: 0;
  background: none;
  border: 0;
  text-align: left;
  font: inherit;
  color: var(--hd-ink);
  cursor: pointer;
}
.row-btn.is-static { cursor: default; }

.row-label { font-size: var(--hd-t-body); }

.hd-label { margin: 0 0 10px; }

.skins { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; max-width: 30em; margin: 0 0 28px; }

/* Border-box and a padding that shrinks by exactly the pixel the selected
   border grows: without it, choosing one nudges both previews sideways. */
.skin {
  box-sizing: border-box;
  display: block;
  width: 100%;
  padding: 7px;
  background: none;
  border: 1px solid var(--hd-feint);
  border-radius: 3px;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.skin.is-on { padding: 6px; border: 2px solid var(--hd-margin); }

.preview { display: block; height: 92px; position: relative; overflow: hidden; }

/* Drawn from the same tokens the real sheet uses, so the picker cannot drift
   from what it is promising. 13px between rules rather than the sheet's 28,
   because this is the page seen small — at full pitch a 92px preview holds
   three lines and reads as a card, not as ruled paper. */
.preview.is-book {
  background-color: var(--hd-paper);
  background-image: linear-gradient(var(--hd-feint) 1px, transparent 1px);
  background-size: 100% 13px;
}
.preview.is-book::after {
  content: '';
  position: absolute;
  top: 0; bottom: 0; left: 17px;
  width: 1px;
  background: var(--hd-margin);
}

/* Mixed rather than pinned to a hex: --hd-paper is itself one of the four
   tokens the tin redefines, so a fixed colour here would be the right ground
   in the book and the wrong one the moment the tin is chosen. Derived, the
   pair stays a light page beside a darker one in either skin. */
.preview.is-tin { background: color-mix(in srgb, var(--hd-paper) 82%, var(--hd-ink)); }

.filed-card {
  position: absolute;
  top: 10px; right: 8px; bottom: 10px; left: 8px;
  background: var(--hd-sheet);
  border-radius: 2px;
  box-shadow: 0 1px 3px color-mix(in srgb, var(--hd-ink) 30%, transparent);
  overflow: hidden;
}
.filed-card::before {
  content: '';
  position: absolute;
  top: 0; right: 0; left: 0;
  height: 12px;
  background: var(--hd-margin);
}

.skin-label {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 8px;
  font-family: var(--hd-sans);
  font-size: var(--hd-t-control);
  color: var(--hd-pencil);
}
.skin.is-on .skin-label { color: var(--hd-margin); }

/* The state, in the sans the numbers use, so it reads as a value rather than
   as more of the sentence. */
.row-state { color: var(--hd-pencil); }
[aria-checked='true'] .row-state { color: var(--hd-margin); }
</style>
